import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Star, Shield, Search, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Location data with postcodes, descriptions, and nearby areas
const LOCATIONS = {
  // Major English Cities
  london: {
    name: 'London',
    region: 'Greater London',
    postcodes: ['E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC', 'EN', 'IG', 'RM', 'DA', 'BR', 'CR', 'SM', 'KT', 'TW', 'UB', 'HA', 'NW'],
    description: 'Find trusted Gas Safe registered engineers across London, from Central London to the outer boroughs.',
    nearby: ['essex', 'kent', 'surrey', 'hertfordshire']
  },
  manchester: {
    name: 'Manchester',
    region: 'Greater Manchester',
    postcodes: ['M', 'SK', 'OL', 'BL', 'WN'],
    description: 'Discover vetted gas engineers serving Manchester and the surrounding Greater Manchester area.',
    nearby: ['liverpool', 'leeds', 'sheffield', 'stoke']
  },
  birmingham: {
    name: 'Birmingham',
    region: 'West Midlands',
    postcodes: ['B', 'WS', 'DY', 'WV'],
    description: 'Connect with qualified gas engineers in Birmingham and across the West Midlands.',
    nearby: ['wolverhampton', 'coventry', 'stoke', 'leicester']
  },
  leeds: {
    name: 'Leeds',
    region: 'West Yorkshire',
    postcodes: ['LS', 'BD', 'WF', 'HX'],
    description: 'Find reliable gas engineers in Leeds and throughout West Yorkshire.',
    nearby: ['sheffield', 'manchester', 'hull', 'newcastle']
  },
  liverpool: {
    name: 'Liverpool',
    region: 'Merseyside',
    postcodes: ['L', 'CH', 'PR'],
    description: 'Trusted gas engineers serving Liverpool and the Merseyside region.',
    nearby: ['manchester', 'stoke', 'birmingham']
  },
  sheffield: {
    name: 'Sheffield',
    region: 'South Yorkshire',
    postcodes: ['S', 'DN'],
    description: 'Professional gas engineers available across Sheffield and South Yorkshire.',
    nearby: ['leeds', 'manchester', 'nottingham', 'derby']
  },
  bristol: {
    name: 'Bristol',
    region: 'South West England',
    postcodes: ['BS', 'BA'],
    description: 'Quality gas engineers serving Bristol and the South West region.',
    nearby: ['cardiff', 'southampton', 'reading', 'plymouth']
  },
  newcastle: {
    name: 'Newcastle',
    region: 'Tyne and Wear',
    postcodes: ['NE', 'SR', 'DH'],
    description: 'Experienced gas engineers in Newcastle upon Tyne and the North East.',
    nearby: ['leeds', 'edinburgh', 'hull']
  },
  nottingham: {
    name: 'Nottingham',
    region: 'East Midlands',
    postcodes: ['NG', 'DE'],
    description: 'Gas Safe registered engineers across Nottingham and the East Midlands.',
    nearby: ['derby', 'leicester', 'sheffield', 'birmingham']
  },
  leicester: {
    name: 'Leicester',
    region: 'Leicestershire',
    postcodes: ['LE'],
    description: 'Find local gas engineers in Leicester and Leicestershire.',
    nearby: ['nottingham', 'birmingham', 'coventry', 'derby']
  },
  coventry: {
    name: 'Coventry',
    region: 'West Midlands',
    postcodes: ['CV'],
    description: 'Trusted gas engineers serving Coventry and surrounding areas.',
    nearby: ['birmingham', 'leicester', 'nottingham']
  },
  brighton: {
    name: 'Brighton',
    region: 'East Sussex',
    postcodes: ['BN'],
    description: 'Professional gas engineers in Brighton and along the South Coast.',
    nearby: ['london', 'southampton', 'portsmouth']
  },
  reading: {
    name: 'Reading',
    region: 'Berkshire',
    postcodes: ['RG', 'SL'],
    description: 'Vetted gas engineers serving Reading and Berkshire.',
    nearby: ['london', 'southampton', 'bristol', 'surrey']
  },
  southampton: {
    name: 'Southampton',
    region: 'Hampshire',
    postcodes: ['SO', 'PO'],
    description: 'Reliable gas engineers across Southampton and Hampshire.',
    nearby: ['portsmouth', 'brighton', 'reading', 'bristol']
  },
  portsmouth: {
    name: 'Portsmouth',
    region: 'Hampshire',
    postcodes: ['PO'],
    description: 'Gas Safe engineers serving Portsmouth and the surrounding area.',
    nearby: ['southampton', 'brighton', 'reading']
  },
  plymouth: {
    name: 'Plymouth',
    region: 'Devon',
    postcodes: ['PL', 'EX', 'TQ'],
    description: 'Find gas engineers in Plymouth and across Devon.',
    nearby: ['bristol', 'southampton']
  },
  stoke: {
    name: 'Stoke-on-Trent',
    region: 'Staffordshire',
    postcodes: ['ST'],
    description: 'Trusted gas engineers in Stoke-on-Trent and Staffordshire.',
    nearby: ['manchester', 'birmingham', 'derby', 'liverpool']
  },
  wolverhampton: {
    name: 'Wolverhampton',
    region: 'West Midlands',
    postcodes: ['WV'],
    description: 'Gas Safe registered engineers serving Wolverhampton.',
    nearby: ['birmingham', 'stoke', 'coventry']
  },
  derby: {
    name: 'Derby',
    region: 'Derbyshire',
    postcodes: ['DE'],
    description: 'Professional gas engineers in Derby and Derbyshire.',
    nearby: ['nottingham', 'sheffield', 'leicester', 'stoke']
  },
  hull: {
    name: 'Hull',
    region: 'East Yorkshire',
    postcodes: ['HU'],
    description: 'Find trusted gas engineers in Hull and East Yorkshire.',
    nearby: ['leeds', 'sheffield', 'newcastle']
  },
  // Counties
  essex: {
    name: 'Essex',
    region: 'South East England',
    postcodes: ['SS', 'CM', 'CO', 'RM', 'IG', 'EN'],
    description: 'Gas Safe registered engineers across Essex, from Southend to Colchester.',
    nearby: ['london', 'kent', 'hertfordshire']
  },
  kent: {
    name: 'Kent',
    region: 'South East England',
    postcodes: ['CT', 'ME', 'TN', 'DA', 'BR'],
    description: 'Trusted gas engineers throughout Kent, the Garden of England.',
    nearby: ['london', 'essex', 'surrey', 'brighton']
  },
  surrey: {
    name: 'Surrey',
    region: 'South East England',
    postcodes: ['GU', 'KT', 'RH', 'SM', 'CR', 'TW'],
    description: 'Quality gas engineers serving Surrey and surrounding areas.',
    nearby: ['london', 'kent', 'reading', 'brighton']
  },
  hertfordshire: {
    name: 'Hertfordshire',
    region: 'East of England',
    postcodes: ['AL', 'EN', 'SG', 'WD', 'HP'],
    description: 'Find gas engineers across Hertfordshire.',
    nearby: ['london', 'essex', 'reading']
  },
  // Scotland
  edinburgh: {
    name: 'Edinburgh',
    region: 'Scotland',
    postcodes: ['EH'],
    description: 'Professional gas engineers in Edinburgh and the Lothians.',
    nearby: ['glasgow', 'newcastle', 'aberdeen']
  },
  glasgow: {
    name: 'Glasgow',
    region: 'Scotland',
    postcodes: ['G', 'PA'],
    description: 'Trusted gas engineers serving Glasgow and the West of Scotland.',
    nearby: ['edinburgh', 'aberdeen']
  },
  aberdeen: {
    name: 'Aberdeen',
    region: 'Scotland',
    postcodes: ['AB'],
    description: 'Gas Safe registered engineers in Aberdeen and Aberdeenshire.',
    nearby: ['edinburgh', 'glasgow']
  },
  // Wales
  cardiff: {
    name: 'Cardiff',
    region: 'Wales',
    postcodes: ['CF', 'NP'],
    description: 'Find qualified gas engineers in Cardiff and South Wales.',
    nearby: ['swansea', 'bristol', 'birmingham']
  },
  swansea: {
    name: 'Swansea',
    region: 'Wales',
    postcodes: ['SA'],
    description: 'Trusted gas engineers serving Swansea and West Wales.',
    nearby: ['cardiff', 'bristol']
  },
  // Northern Ireland
  belfast: {
    name: 'Belfast',
    region: 'Northern Ireland',
    postcodes: ['BT'],
    description: 'Professional gas engineers in Belfast and across Northern Ireland.',
    nearby: []
  }
};

const LocationPage = () => {
  const { location } = useParams();
  const navigate = useNavigate();
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPostcode, setSearchPostcode] = useState('');

  const locationData = LOCATIONS[location?.toLowerCase()];

  useEffect(() => {
    if (locationData) {
      fetchEngineers();
    }
  }, [location]);

  const fetchEngineers = async () => {
    setLoading(true);
    try {
      // Search using the first postcode prefix for this location
      const response = await axios.get(`${API}/engineers/search`, {
        params: { postcode: locationData.postcodes[0] }
      });
      setEngineers(response.data.engineers || []);
    } catch (error) {
      console.error('Error fetching engineers:', error);
      setEngineers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchPostcode.trim()) {
      navigate(`/find-engineer?postcode=${encodeURIComponent(searchPostcode)}`);
    }
  };

  // 404 for invalid locations
  if (!locationData) {
    return (
      <div className="location-page">
        <div className="container">
          <div className="not-found">
            <h1>Location Not Found</h1>
            <p>Sorry, we don't have a dedicated page for this location yet.</p>
            <Button onClick={() => navigate('/find-engineer')}>
              Search All Engineers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Schema.org structured data for local business search
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Gas Engineers in ${locationData.name} | The Gas Guide`,
    "description": locationData.description,
    "url": `https://thegasguide.co.uk/find-engineer/${location}`,
    "mainEntity": {
      "@type": "ItemList",
      "name": `Gas Safe Registered Engineers in ${locationData.name}`,
      "description": `Directory of vetted, Gas Safe registered engineers serving ${locationData.name} and ${locationData.region}`,
      "numberOfItems": engineers.length,
      "itemListElement": engineers.slice(0, 10).map((eng, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "LocalBusiness",
          "name": eng.businessName,
          "description": `Gas Safe registered engineer serving ${locationData.name}`,
          "telephone": eng.phone,
          "email": eng.email,
          "areaServed": locationData.name,
          "priceRange": "££"
        }
      }))
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thegasguide.co.uk" },
        { "@type": "ListItem", "position": 2, "name": "Find an Engineer", "item": "https://thegasguide.co.uk/find-engineer" },
        { "@type": "ListItem", "position": 3, "name": locationData.name, "item": `https://thegasguide.co.uk/find-engineer/${location}` }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>Gas Engineers in {locationData.name} | Find Trusted Local Engineers | The Gas Guide</title>
        <meta name="description" content={`Find Gas Safe registered engineers in ${locationData.name}. ${locationData.description} Vetted, trusted professionals for boiler repairs, installations & more.`} />
        <meta name="keywords" content={`gas engineer ${locationData.name}, gas safe engineer ${locationData.name}, boiler repair ${locationData.name}, heating engineer ${locationData.name}, ${locationData.region} gas engineer`} />
        <link rel="canonical" href={`https://thegasguide.co.uk/find-engineer/${location}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`Gas Engineers in ${locationData.name} | The Gas Guide`} />
        <meta property="og:description" content={locationData.description} />
        <meta property="og:url" content={`https://thegasguide.co.uk/find-engineer/${location}`} />
        <meta property="og:type" content="website" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <div className="location-page">
        {/* Hero Section */}
        <section className="location-hero">
          <div className="container">
            {/* Breadcrumbs */}
            <nav className="breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={14} />
              <Link to="/find-engineer">Find an Engineer</Link>
              <ChevronRight size={14} />
              <span>{locationData.name}</span>
            </nav>

            <h1>Gas Engineers in {locationData.name}</h1>
            <p className="location-subtitle">{locationData.description}</p>
            
            {/* Search Box */}
            <form onSubmit={handleSearch} className="location-search">
              <div className="search-input-wrapper">
                <MapPin className="search-icon" size={20} />
                <Input
                  type="text"
                  placeholder="Enter your postcode for exact results..."
                  value={searchPostcode}
                  onChange={(e) => setSearchPostcode(e.target.value)}
                  className="location-search-input"
                />
              </div>
              <Button type="submit" className="btn-primary">
                <Search size={18} />
                Search
              </Button>
            </form>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="location-trust">
          <div className="container">
            <div className="trust-badges">
              <div className="trust-badge">
                <Shield size={24} />
                <span>All engineers Gas Safe verified</span>
              </div>
              <div className="trust-badge">
                <Star size={24} />
                <span>Vetted & reviewed by customers</span>
              </div>
              <div className="trust-badge">
                <MapPin size={24} />
                <span>Local to {locationData.name}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Engineers List */}
        <section className="location-engineers">
          <div className="container">
            <h2>Available Engineers in {locationData.name}</h2>
            
            {loading ? (
              <div className="loading-state">
                <p>Searching for engineers in {locationData.name}...</p>
              </div>
            ) : engineers.length > 0 ? (
              <div className="engineers-grid">
                {engineers.map(engineer => (
                  <Card key={engineer.id} className="engineer-location-card">
                    <CardHeader>
                      <div className="engineer-card-top">
                        <CardTitle>{engineer.fullName}</CardTitle>
                        {engineer.isFoundingMember && (
                          <Badge className="founding-badge">Founding Member</Badge>
                        )}
                      </div>
                      <p className="business-name">{engineer.businessName}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="engineer-stats">
                        <div className="stat">
                          <Star fill="currentColor" size={16} />
                          <span>{engineer.rating || '5.0'}</span>
                          <span className="stat-label">({engineer.reviewCount || 0} reviews)</span>
                        </div>
                        <div className="stat">
                          <Shield size={16} />
                          <span>{engineer.yearsTrading} years exp.</span>
                        </div>
                      </div>
                      
                      {engineer.services?.length > 0 && (
                        <div className="engineer-service-tags">
                          {engineer.services.slice(0, 3).map((service, idx) => (
                            <Badge key={idx} variant="outline" className="service-tag">
                              {service === 'domestic' ? 'Domestic' :
                               service === 'commercial' ? 'Commercial' :
                               service === 'lpg' ? 'LPG' :
                               service === 'landlord' ? 'Landlord' : service}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="engineer-contact">
                        <a href={`tel:${engineer.phone}`} className="contact-btn">
                          <Phone size={16} />
                          Call Now
                        </a>
                        <a href={`mailto:${engineer.email}`} className="contact-btn secondary">
                          <Mail size={16} />
                          Email
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="no-engineers">
                <h3>No Engineers Listed Yet in {locationData.name}</h3>
                <p>We're growing our network in this area. Try searching with your specific postcode or check nearby areas.</p>
                <Button onClick={() => navigate('/find-engineer')} className="btn-primary">
                  Search All Areas
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Nearby Areas */}
        {locationData.nearby.length > 0 && (
          <section className="nearby-areas">
            <div className="container">
              <h2>Nearby Areas</h2>
              <div className="nearby-links">
                {locationData.nearby.map(nearbyKey => (
                  <Link 
                    key={nearbyKey} 
                    to={`/find-engineer/${nearbyKey}`}
                    className="nearby-link"
                  >
                    <MapPin size={16} />
                    Gas Engineers in {LOCATIONS[nearbyKey]?.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SEO Content */}
        <section className="location-seo-content">
          <div className="container">
            <h2>Finding a Gas Engineer in {locationData.name}</h2>
            <div className="seo-content-grid">
              <div className="seo-block">
                <h3>Why Choose The Gas Guide?</h3>
                <p>All engineers listed on The Gas Guide are Gas Safe registered and have been vetted to ensure they meet our high standards. When you need a gas engineer in {locationData.name}, you can trust that you're getting a qualified professional.</p>
              </div>
              <div className="seo-block">
                <h3>Services Available in {locationData.name}</h3>
                <ul>
                  <li>Boiler repairs and servicing</li>
                  <li>New boiler installations</li>
                  <li>Central heating systems</li>
                  <li>Gas fire servicing</li>
                  <li>Landlord gas safety certificates (CP12)</li>
                  <li>Emergency gas repairs</li>
                </ul>
              </div>
              <div className="seo-block">
                <h3>Postcodes Covered</h3>
                <p>Our {locationData.name} engineers cover the following postcode areas: <strong>{locationData.postcodes.join(', ')}</strong></p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="location-cta">
          <div className="container">
            <h2>Are You a Gas Engineer in {locationData.name}?</h2>
            <p>Join The Gas Guide and connect with customers in your area. Get listed in our trusted directory of Gas Safe engineers.</p>
            <Button onClick={() => navigate('/for-engineers')} className="btn-primary btn-large">
              Apply to Join
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default LocationPage;
